import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import DashboardLayout from '@/pages/dashboard/components/DashboardLayout';
import { inventoryApi } from '@/services/api';
import { useCategories, useCurrentUser, useUpdateInventoryItem } from '@/hooks/useQueries';
import type { InventoryItem, Category } from '@/types/index.ts';
import { handleApiError } from '@/utils/errorHandler.ts';

// Transformed item for display (includes computed status)
interface InventoryDisplayItem {
  id: number;
  name: string;
  sku: string;
  barcode: string;
  category: string;
  location: string;
  locationStocks?: Array<{ location_id: number; location_name: string; quantity: number }>; // Per-location distribution
  quantity: number;
  minStock: number;
  unit: string;
  unit_price: number;
  cost_price: number;
  status: 'normal' | 'low';
  updatedAt?: string;
}

export default function Inventory() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [items, setItems] = useState<InventoryDisplayItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [editingMinStock, setEditingMinStock] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [editingPrices, setEditingPrices] = useState<number | null>(null);
  const [editingCostPrice, setEditingCostPrice] = useState<string>('');
  const [editingUnitPrice, setEditingUnitPrice] = useState<string>('');
  const [updating, setUpdating] = useState<number | null>(null);
  
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  const { data: categoriesData } = useCategories({ per_page: 100, status: 'active' });
  const updateItemMutation = useUpdateInventoryItem();
  
  const isAdmin = currentUser?.role === 'admin';
  
  const categoriesList = categoriesData?.data ?? (Array.isArray(categoriesData) ? categoriesData : []) as Category[];
  const categories = ['all', ...(categoriesList || []).map((cat: Category) => cat?.name || '').filter(Boolean)];

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, filterCategory, filterStatus, currentPage, itemsPerPage, categoriesData]);

  const loadItems = async () => {
    try {
      setLoading(true);
      
      const useClientSidePagination = filterStatus === 'normal';
      
      const params: any = {
        status: 'active',
      };

      if (useClientSidePagination) {
        params.per_page = 1000;
        params.page = 1;
      } else {
        params.page = currentPage;
        params.per_page = itemsPerPage;
      }

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      if (filterCategory !== 'all' && categoriesList.length > 0) {
        const category = categoriesList.find((cat: Category) => cat.name === filterCategory);
        if (category) {
          params.category_id = category.id;
        }
      }

      if (filterStatus === 'low') {
        params.low_stock = true;
      }

      const response = await inventoryApi.getItems(params);
      const responseData = response as any;
      const itemsData = responseData.data?.data ?? (Array.isArray(responseData.data) ? responseData.data : []);
      
      const transformedItems: InventoryDisplayItem[] = itemsData.map((item: InventoryItem) => ({
        id: item.id,
        name: item.name,
        sku: item.sku || 'N/A',
        barcode: item.barcode || 'N/A',
        category: item.category?.name || 'Uncategorized',
        location: item.location?.name || 'N/A',
        locationStocks: item.location_stocks || [],
        quantity: item.quantity || 0,
        minStock: item.min_quantity || 0,
        unit: item.unit || 'units',
        unit_price: item.unit_price ?? 0,
        cost_price: item.cost_price ?? 0,
        status: (item.quantity || 0) <= (item.min_quantity || 0) ? 'low' : 'normal',
        updatedAt: item.updated_at,
      }));

      let filteredItems = transformedItems;
      
      if (filterStatus === 'normal') {
        filteredItems = transformedItems.filter((item: InventoryDisplayItem) => item.status === 'normal');
      } else if (filterStatus === 'low') {
        filteredItems = transformedItems.filter((item: InventoryDisplayItem) => item.status === 'low');
      }

      if (useClientSidePagination) {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        filteredItems = filteredItems.slice(startIndex, endIndex);
      }

      setItems(filteredItems);

      if (useClientSidePagination) {
        const allNormalItems = transformedItems.filter((item: InventoryDisplayItem) => item.status === 'normal');
        setTotalItems(allNormalItems.length);
        setTotalPages(Math.max(1, Math.ceil(allNormalItems.length / itemsPerPage)));
      } else {
        if (responseData.meta) {
          setTotalPages(responseData.meta.last_page || 1);
          setTotalItems(responseData.meta.total || filteredItems.length);
        } else {
          setTotalPages(1);
          setTotalItems(filteredItems.length);
        }
      }
    } catch (error) {
      console.error('Error loading items:', error);
      setItems([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = () => {
    setCurrentPage(1);
  };

  const handleExport = async () => {
    try {
      // Fetch all items matching current filters for export
      const params: any = {
        status: 'active',
        per_page: 10000, // Large number to get all items
        page: 1,
      };

      if (searchQuery.trim()) {
        params.search = searchQuery.trim();
      }

      if (filterCategory !== 'all' && categoriesList.length > 0) {
        const category = categoriesList.find((cat: Category) => cat.name === filterCategory);
        if (category) {
          params.category_id = category.id;
        }
      }

      if (filterStatus === 'low') {
        params.low_stock = true;
      }

      const response = await inventoryApi.getItems(params);
      const responseData = response as any;
      const itemsData = responseData.data?.data ?? (Array.isArray(responseData.data) ? responseData.data : []);
      
      // Transform items for export
          const transformedItems: InventoryDisplayItem[] = itemsData.map((item: InventoryItem) => ({
        name: item.name || '',
        sku: item.sku || 'N/A',
        barcode: item.barcode || 'N/A',
        category: item.category?.name || 'Uncategorized',
        location: item.location?.name || 'N/A',
        quantity: item.quantity || 0,
        minStock: item.min_quantity || 0,
        status: (item.quantity || 0) <= (item.min_quantity || 0) ? 'Low' : 'Normal',
        updatedAt: item.updated_at || item.created_at || '',
      }));

      // Apply status filter if needed.
      // The export transform above sets status to 'Normal' / 'Low' (title-case),
      // so comparisons must use the same casing.
      let exportItems = transformedItems;
      if (filterStatus === 'normal') {
        exportItems = transformedItems.filter((item: InventoryDisplayItem) => item.status === 'Normal');
      } else if (filterStatus === 'low') {
        exportItems = transformedItems.filter((item: InventoryDisplayItem) => item.status === 'Low');
      }

      // Prepare CSV content
      const headers = ['Item Name', 'Barcode', 'SKU', 'Category', 'Location', 'Current Stock', 'Minimum Stock', 'Status', 'Last Updated'];
      const csvContent = [
        headers.join(','),
          ...exportItems.map((item: InventoryDisplayItem) =>
          [
            `"${item.name}"`,
            `"${item.barcode}"`,
            `"${item.sku}"`,
            `"${item.category}"`,
            `"${item.location}"`,
            item.quantity,
            item.minStock,
            `"${item.status}"`,
            item.updatedAt ? new Date(item.updatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : (item as any).updated_at ? new Date((item as any).updated_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : ''
          ].join(',')
        )
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `inventory-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting inventory:', error);
      alert('Failed to export inventory. Please try again.');
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const handleEditMinStock = (item: InventoryDisplayItem) => {
    setEditingMinStock(item.id);
    setEditingValue(item.minStock.toString());
  };

  const handleCancelEdit = () => {
    setEditingMinStock(null);
    setEditingValue('');
  };

  const handleSaveMinStock = async (item: InventoryDisplayItem) => {
    const newMinStock = parseInt(editingValue);
    
    if (isNaN(newMinStock) || newMinStock < 0) {
      handleCancelEdit();
      return;
    }

    if (newMinStock === item.minStock) {
      handleCancelEdit();
      return;
    }

    try {
      setUpdating(item.id);
      await updateItemMutation.mutateAsync({
        id: item.id,
        data: { min_quantity: newMinStock },
      });
      
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      await loadItems();
      
      setEditingMinStock(null);
      setEditingValue('');
    } catch (error) {
      const errorDetails = handleApiError(error, 'Update Minimum Stock');
      alert(errorDetails.message);
    } finally {
      setUpdating(null);
    }
  };

  const handleEditPrices = (item: InventoryDisplayItem) => {
    setEditingPrices(item.id);
    setEditingCostPrice(item.cost_price > 0 ? item.cost_price.toFixed(2) : '');
    setEditingUnitPrice(item.unit_price > 0 ? item.unit_price.toFixed(2) : '');
    setEditingMinStock(null);
  };

  const handleCancelPriceEdit = () => {
    setEditingPrices(null);
    setEditingCostPrice('');
    setEditingUnitPrice('');
  };

  const handleSavePrices = async (item: InventoryDisplayItem) => {
    const newCostPrice = parseFloat(editingCostPrice);
    const newUnitPrice = parseFloat(editingUnitPrice);

    if (isNaN(newUnitPrice) || newUnitPrice < 0) {
      handleCancelPriceEdit();
      return;
    }

    const data: { cost_price?: number; unit_price?: number } = {};
    if (!isNaN(newCostPrice) && newCostPrice >= 0) data.cost_price = newCostPrice;
    if (!isNaN(newUnitPrice) && newUnitPrice >= 0) data.unit_price = newUnitPrice;

    if (Object.keys(data).length === 0) {
      handleCancelPriceEdit();
      return;
    }

    try {
      setUpdating(item.id);
      await updateItemMutation.mutateAsync({ id: item.id, data });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      await loadItems();
      handleCancelPriceEdit();
    } catch (error) {
      const errorDetails = handleApiError(error, 'Update Prices');
      alert(errorDetails.message);
    } finally {
      setUpdating(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto space-y-6 px-4 sm:px-6 lg:pr-4">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
        </div>

        {/* Filters - Compact Design */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex flex-col sm:flex-row gap-3 items-end">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    handleFilterChange();
                  }}
                  placeholder="Search items, barcode, SKU..."
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#50b3e1] focus:border-[#50b3e1]"
                />
              </div>
            </div>
            
            {/* Category Filter */}
            <div className="sm:w-48">
              <select
                value={filterCategory}
                onChange={(e) => {
                  setFilterCategory(e.target.value);
                  handleFilterChange();
                }}
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#50b3e1] focus:border-[#50b3e1] cursor-pointer bg-white"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Status Filter */}
            <div className="sm:w-40">
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  handleFilterChange();
                }}
                className="w-full px-4 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#50b3e1] focus:border-[#50b3e1] cursor-pointer bg-white"
              >
                <option value="all">All Status</option>
                <option value="low">Low Stock</option>
                <option value="normal">Normal</option>
              </select>
            </div>

            {/* Export Button */}
            <button 
              onClick={handleExport}
              className="px-3 py-2 bg-[#0997a4] text-white text-sm font-medium hover:bg-[#087d89] transition-colors whitespace-nowrap flex items-center gap-1.5 cursor-pointer rounded"
            >
              <i className="ri-download-line text-sm"></i>
              Export
            </button>
          </div>
        </div>

        {/* Inventory Table */}
        <div className="bg-white border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Item</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Barcode</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Category</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Stock</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Prices</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-2 border-[#50b3e1] border-t-transparent"></div>
                        <p className="mt-3 text-sm text-gray-500">Loading...</p>
                      </div>
                    </td>
                  </tr>
                ) : items.length > 0 ? (
                  items.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-4">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{item.name}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{item.sku}</p>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-sm text-gray-900 font-mono">{item.barcode}</p>
                      </td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-gray-100 text-gray-800">
                          {String(item.category)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {item.locationStocks && item.locationStocks.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {item.locationStocks.map((stock, idx) => (
                              <span 
                                key={idx} 
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-[#50b3e1]/10 border border-[#50b3e1]/30 rounded text-[10px] leading-tight whitespace-nowrap"
                                title={`${stock.location_name}: ${stock.quantity} ${item.unit}`}
                              >
                                <span className="font-medium text-[#0997a4]">{stock.location_name}</span>
                                <span className="text-gray-400 text-[8px]">•</span>
                                <span className="font-semibold text-gray-900">{stock.quantity}</span>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-900">{String(item.location)}</p>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {item.quantity} <span className="text-gray-500 font-normal text-xs">{item.unit}</span>
                          </p>
                          {editingMinStock === item.id ? (
                            <div className="flex items-center gap-1.5 mt-2">
                              <input
                                type="number"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleSaveMinStock(item);
                                  } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    handleCancelEdit();
                                  }
                                }}
                                className="w-20 px-2 py-1 text-xs border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#50b3e1] focus:border-[#50b3e1]"
                                min="0"
                                step="1"
                                autoFocus
                                disabled={updating === item.id}
                              />
                              <button
                                onClick={() => handleSaveMinStock(item)}
                                disabled={updating === item.id}
                                className="w-6 h-6 flex items-center justify-center bg-[#50b3e1] hover:bg-[#3a9bc1] text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Save"
                              >
                                {updating === item.id ? (
                                  <i className="ri-loader-4-line animate-spin text-xs"></i>
                                ) : (
                                  <i className="ri-check-line text-xs"></i>
                                )}
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                disabled={updating === item.id}
                                className="w-6 h-6 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Cancel"
                              >
                                <i className="ri-close-line text-xs"></i>
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5 mt-1">
                              <p className="text-xs text-gray-500">Min: {item.minStock}</p>
                              {isAdmin && (
                                <button
                                  onClick={() => handleEditMinStock(item)}
                                  className="text-gray-400 hover:text-[#50b3e1] transition-colors"
                                  title="Edit minimum stock"
                                >
                                  <i className="ri-edit-line text-xs"></i>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      {/* Prices column */}
                      <td className="px-4 py-4">
                        {editingPrices === item.id ? (
                          <div className="space-y-1.5 min-w-[160px]">
                            <div>
                              <p className="text-[10px] text-gray-500 mb-0.5">Cost (JMD)</p>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={editingCostPrice}
                                onChange={(e) => setEditingCostPrice(e.target.value)}
                                placeholder="0.00"
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#50b3e1]"
                              />
                            </div>
                            <div>
                              <p className="text-[10px] text-gray-500 mb-0.5">Selling (JMD)</p>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={editingUnitPrice}
                                onChange={(e) => setEditingUnitPrice(e.target.value)}
                                placeholder="0.00"
                                className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-[#50b3e1]"
                              />
                            </div>
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleSavePrices(item)}
                                disabled={updating === item.id}
                                className="flex-1 px-2 py-1 bg-[#0997a4] text-white text-xs rounded hover:bg-[#087d89] disabled:opacity-50"
                              >
                                {updating === item.id ? '...' : 'Save'}
                              </button>
                              <button
                                onClick={handleCancelPriceEdit}
                                className="flex-1 px-2 py-1 border border-gray-300 text-xs rounded hover:bg-gray-50"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start gap-2">
                            <div>
                              {item.cost_price > 0 && (
                                <p className="text-xs text-gray-500">Cost: <span className="font-medium text-gray-700">JMD {item.cost_price.toLocaleString('en-JM', { minimumFractionDigits: 2 })}</span></p>
                              )}
                              <p className="text-xs text-gray-700">Sell: <span className="font-semibold text-gray-900">JMD {item.unit_price.toLocaleString('en-JM', { minimumFractionDigits: 2 })}</span></p>
                            </div>
                            {isAdmin && (
                              <button
                                onClick={() => handleEditPrices(item)}
                                className="text-gray-400 hover:text-[#0997a4] transition-colors mt-0.5"
                                title="Edit prices"
                              >
                                <i className="ri-edit-line text-xs"></i>
                              </button>
                            )}
                          </div>
                        )}
                      </td>

                      <td className="px-4 py-4">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          item.status === 'low' 
                            ? 'bg-red-50 text-[#eb1539]' 
                            : 'bg-green-50 text-green-700'
                        }`}>
                          {item.status === 'low' ? 'Low' : 'Normal'}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-4 py-16 text-center">
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <i className="ri-inbox-line text-gray-400 text-2xl"></i>
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-1">No items found</h3>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalItems > 0 && (
          <div className="bg-white border border-gray-200 p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-sm text-gray-700">
                <span>Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-2 py-1.5 border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#50b3e1] focus:border-[#50b3e1] cursor-pointer bg-white text-sm"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>entries</span>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-2.5 py-1.5 text-sm border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#50b3e1] transition-colors"
                  title="First page"
                >
                  «
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-2.5 py-1.5 text-sm border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#50b3e1] transition-colors"
                  title="Previous page"
                >
                  ‹
                </button>
                {getPageNumbers().map((page, index) => (
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className="px-2.5 py-1.5 text-sm text-gray-400">
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page as number)}
                      className={`px-3 py-1.5 text-sm border transition-colors focus:outline-none focus:ring-1 focus:ring-[#50b3e1] ${
                        currentPage === page
                          ? 'bg-[#50b3e1] text-white border-[#50b3e1]'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  )
                ))}
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-2.5 py-1.5 text-sm border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#50b3e1] transition-colors"
                  title="Next page"
                >
                  ›
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-2.5 py-1.5 text-sm border border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-[#50b3e1] transition-colors"
                  title="Last page"
                >
                  »
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
